import React, { useState, useEffect, useRef } from "react";
import { RECOMMENDATION_NOTES } from "../../../constants/validation";
import { CannedReasonButtons } from "./CannedReasonButtons";

export interface BulkRejectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (notes: string) => void;
  selectedCount: number;
  isLoading: boolean;
}

/**
 * Modal for bulk rejection with shared reason
 * Requires a rejection reason (10-500 characters)
 */
export function BulkRejectModal({
  isOpen,
  onClose,
  onSubmit,
  selectedCount,
  isLoading,
}: BulkRejectModalProps) {
  const [notes, setNotes] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const charCount = notes.length;
  const isValid =
    charCount >= RECOMMENDATION_NOTES.MIN_LENGTH &&
    charCount <= RECOMMENDATION_NOTES.MAX_LENGTH;

  // Focus textarea on open
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setNotes("");
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen && !isLoading) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isLoading, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid && !isLoading) {
      onSubmit(notes);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      aria-labelledby="bulk-reject-title"
      aria-modal="true"
      role="dialog"
    >
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={isLoading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all"
          data-testid="bulk-reject-modal"
        >
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2
                id="bulk-reject-title"
                className="text-lg font-semibold text-gray-900"
              >
                Odrzuć {selectedCount}{" "}
                {selectedCount === 1
                  ? "rekomendację"
                  : selectedCount < 5
                    ? "rekomendacje"
                    : "rekomendacji"}
              </h2>
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="rounded-md p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                aria-label="Zamknij"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <label
                htmlFor="bulk-reject-notes"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Podaj powód odrzucenia (wspólny dla wszystkich):
              </label>
              <textarea
                ref={textareaRef}
                id="bulk-reject-notes"
                data-testid="bulk-reject-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={isLoading}
                rows={4}
                className={`
                  w-full rounded-md border px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500
                  disabled:bg-gray-100 disabled:cursor-not-allowed
                  ${
                    charCount > 0 && !isValid
                      ? "border-red-300"
                      : "border-gray-300"
                  }
                `}
                placeholder="Wpisz powód odrzucenia..."
                maxLength={RECOMMENDATION_NOTES.MAX_LENGTH}
              />
              {/* Canned reason helper buttons */}
              <CannedReasonButtons
                value={notes}
                onAppend={setNotes}
                maxLength={RECOMMENDATION_NOTES.MAX_LENGTH}
                disabled={isLoading}
                textareaRef={textareaRef}
                variant="gray"
              />
              <div
                className={`mt-1 text-xs ${
                  charCount > 0 && charCount < RECOMMENDATION_NOTES.MIN_LENGTH
                    ? "text-red-600"
                    : "text-gray-500"
                }`}
              >
                {charCount} / {RECOMMENDATION_NOTES.MAX_LENGTH} znaków (min.{" "}
                {RECOMMENDATION_NOTES.MIN_LENGTH})
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                data-testid="bulk-reject-cancel"
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={!isValid || isLoading}
                data-testid="bulk-reject-submit"
                className={`
                  inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium
                  focus:outline-none focus:ring-2 focus:ring-red-500
                  ${
                    isValid && !isLoading
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-gray-100 text-gray-400 cursor-not-allowed"
                  }
                `}
              >
                {isLoading && (
                  <span
                    className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                    aria-hidden="true"
                  />
                )}
                Odrzuć
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BulkRejectModal;
